import { useRef } from 'react';
import { EmojiInsertButton } from '@/features/campaigns/ui/message-field-with-emoji';
import { formatCount, MERGE_FIELDS } from '@/features/marketing-shared';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Tooltip } from '@/shared/ui/tooltip';
import {
  DEFAULT_SMS_TEXT_RULES,
  SMS_API_MAX_CHARS,
  SMS_CHARS_PER_CREDIT,
  measureSmsText,
  measureSmsTextFor,
  type SmsTextRules,
} from '../model/text';

function smsCountLabel(parts: number): string {
  if (parts <= 0) return 'Nenhum SMS';
  if (parts === 1) return '1 SMS';
  return `${formatCount(parts)} SMS`;
}

function insertAt(value: string, start: number, end: number, token: string, spaced: boolean) {
  const before = value.slice(0, start);
  const after = value.slice(end);
  const chunk = spaced && before.length > 0 && !/\s$/.test(before) ? ` ${token}` : token;
  const next = `${before}${chunk}${after}`;
  return { next, caret: before.length + chunk.length };
}

export function SmsComposer({
  id = 'texto-sms',
  value,
  onChange,
  preview,
  storeName,
  rules = DEFAULT_SMS_TEXT_RULES,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  /** Texto final da prévia (personalização de exemplo + instrução de saída). */
  preview: string;
  storeName: string;
  /** Regra de contagem e de texto do provedor do remetente. */
  rules?: SmsTextRules;
}) {
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const caretRef = useRef<{ start: number; end: number } | null>(null);
  const shown = measureSmsTextFor(preview, rules);
  const perChar = rules.counting === 'chars160';
  const suffix = rules.optOutSuffix.trim();
  const raw = measureSmsText(value);
  const blank = !value.trim();
  const parts = blank ? 0 : shown.parts;
  const segmentUnits = blank ? 0 : shown.segmentUnits;
  const segmentLimit = blank ? shown.singlePartLimit : shown.segmentLimit;
  const blocked = raw.exceedsApiMax || raw.strayBraces;
  const ratio = segmentLimit === 0 ? 0 : segmentUnits / segmentLimit;

  const remember = (el: HTMLTextAreaElement) => {
    caretRef.current = {
      start: el.selectionStart ?? el.value.length,
      end: el.selectionEnd ?? el.value.length,
    };
  };

  const place = (token: string, spaced: boolean) => {
    const range = caretRef.current ?? { start: value.length, end: value.length };
    const { next, caret } = insertAt(value, range.start, range.end, token, spaced);
    caretRef.current = { start: caret, end: caret };
    onChange(next);
    requestAnimationFrame(() => {
      const el = fieldRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const countClass = blocked ? 'text-destructive' : parts > 1 ? 'text-warning' : 'text-foreground';
  const barClass = blocked ? 'bg-destructive' : parts > 1 ? 'bg-warning' : 'bg-primary';

  return (
    <div className="min-w-0">
      <div id={`${id}-medida`} className="space-y-2">
        <div className="flex items-start justify-between gap-4">
          <p className="flex min-w-0 items-center gap-1.5 text-sm">
            <span className={cn('font-medium', countClass)}>{smsCountLabel(parts)}</span>
            <span className="text-muted-foreground">
              · {perChar ? `${SMS_CHARS_PER_CREDIT} caracteres por SMS` : shown.encoding === 'gsm-7' ? 'GSM-7' : 'Unicode'}
            </span>
            <Tooltip
              side="top"
              align="start"
              content={
                perChar
                  ? `O provedor desta loja cobra 1 SMS a cada ${SMS_CHARS_PER_CREDIT} caracteres, com ou sem acento ou emoji. O texto da campanha aceita até 2.048 caracteres.`
                  : 'GSM-7 cabe 160 numa mensagem e 153 em cada parte seguinte. Acento, emoji ou qualquer caractere fora desse conjunto passa a mensagem inteira para Unicode: 70 e depois 67. Símbolos como €, { e } ocupam dois espaços no GSM-7. O texto da campanha aceita até 2.048 caracteres.'
              }
            />
          </p>
          <p className="shrink-0 text-right text-sm tabular-nums leading-tight text-muted-foreground">
            <span className="text-foreground">{formatCount(segmentUnits)}</span>
            {` / ${formatCount(segmentLimit)}`}
            {parts > 1 ? <span className="mt-0.5 block text-xs">nesta parte</span> : null}
          </p>
        </div>
        <div
          className="h-1 overflow-hidden rounded-full bg-muted"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={segmentLimit}
          aria-valuenow={Math.min(segmentUnits, segmentLimit)}
          aria-label={
            parts <= 0
              ? 'Mensagem vazia'
              : `${smsCountLabel(parts)}, ${formatCount(segmentUnits)} de ${formatCount(segmentLimit)} nesta parte`
          }
        >
          <div
            className={cn('h-full rounded-full transition-[width] duration-200 ease-out', barClass)}
            style={{ width: `${Math.min(100, Math.max(0, ratio * 100))}%` }}
          />
        </div>
        {blank ? (
          <p className="text-sm text-destructive" role="status">
            Escreva a mensagem para continuar.
          </p>
        ) : null}
        {suffix ? (
          <p className="text-sm text-muted-foreground">
            No envio, “{suffix}” entra no fim da mensagem e conta no tamanho. É como a pessoa sai da lista.
          </p>
        ) : null}
        {rules.stripAccents ? (
          <p className="text-sm text-muted-foreground">O provedor desta loja envia sem acento: a prévia já mostra como chega.</p>
        ) : null}
        {raw.strayBraces ? (
          <p className="text-sm text-destructive" role="status">
            Chaves soltas não entram no texto. Use a personalização no formato {'{{variavel}}'}.
          </p>
        ) : null}
        {raw.exceedsApiMax ? (
          <p className="text-sm text-destructive" role="status">
            O texto passa de {formatCount(SMS_API_MAX_CHARS)} caracteres.
          </p>
        ) : null}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <Label htmlFor={id}>Mensagem</Label>
        <EmojiInsertButton onInsert={(emoji) => place(emoji, false)} />
      </div>

      <Textarea
        ref={fieldRef}
        id={id}
        rows={7}
        value={value}
        placeholder="Escreva a mensagem que a pessoa vai receber…"
        aria-describedby={`${id}-medida`}
        className={cn(
          'mt-3 min-h-[11.5rem] resize-y px-4 py-3.5 leading-relaxed',
          blocked && 'border-destructive/70 focus-visible:border-destructive/70 focus-visible:ring-destructive/30',
        )}
        onChange={(event) => {
          onChange(event.target.value);
          remember(event.target);
        }}
        onSelect={(event) => remember(event.currentTarget)}
        onKeyUp={(event) => remember(event.currentTarget)}
        onClick={(event) => remember(event.currentTarget)}
      />

      <div className="mt-4">
        <p className="text-sm font-medium">Personalização</p>
        <p className="mt-1 text-sm text-muted-foreground">Entra onde está o cursor. Na prévia, vira o exemplo.</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {MERGE_FIELDS.map((field) => {
            const sample = field.token === '{{loja}}' ? storeName.trim() || field.sample : field.sample;
            return (
              <Tooltip key={field.token} content={`Na prévia vira “${sample}”. No envio, o valor é de cada pessoa.`}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  aria-label={`Inserir ${field.label}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => place(field.token, true)}
                >
                  {field.label}
                </Button>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>
  );
}
